import React from 'react';
import { Link } from 'react-router-dom';

import { APP, BASE, LEGAL, SHOW_PRICING, PRICES } from '../../captivision.config';
import { CVSections } from '../../document.component';
import { formatDate, formatPrice } from '../../i18n/language.component';

// The Terms of Use, in English: the text that applies. Every other
// terms.<language>.jsx here is a translation of it, with the same sections in
// the same order, and says that this English text is the one that applies.
// While pricing is public it includes the CaptiVision Pro subscription terms
// Apple requires alongside the paywall.

const monthly = formatPrice('en', PRICES.monthly);
const yearly = formatPrice('en', PRICES.yearly);

const SECTIONS = [
    {
        id: 'eula',
        title: 'License agreement',
        body: <>
            <p>
                CaptiVision Client and CaptiVision Host (the "Apps") are licensed, not sold, to you by
                {' '}{LEGAL.companyLegalName} ("we", "us"). Your use of the Apps is governed by Apple's
                {' '}<a href={APP.appleEulaUrl} rel="noopener noreferrer">Licensed Application End User License Agreement</a>
                {' '}(the "Standard EULA"), together with the additional terms on this page. Where the two differ,
                the Standard EULA applies, except where it allows us to set our own terms.
            </p>
            <p>
                The Standard EULA is an agreement between you and us, not Apple. Apple has no obligation to
                provide support or maintenance for the Apps. By downloading or using the Apps you accept these terms.
            </p>

            <h3> What the Standard EULA says, in brief </h3>
            <p> This summary is for convenience only. The full text of the Standard EULA controls. </p>
            <ul>
                <li><strong>Your license.</strong> You may use the Apps on Apple devices that you own or control, as allowed by the App Store's Usage Rules. You may not rent, lease, lend, sell or redistribute the Apps.</li>
                <li><strong>Restrictions.</strong> You may not copy, reverse-engineer, decompile, or try to access the source code of the Apps, except where the law allows it.</li>
                <li><strong>Technical data.</strong> We may use technical information about your device to provide updates and support. CaptiVision does not, in practice, collect such data; see the <Link to={`${BASE}/privacy`}>Privacy Policy</Link>.</li>
                <li><strong>Ending the license.</strong> The license lasts until you or we end it. It ends automatically if you do not follow these terms, and you must then stop using the Apps.</li>
                <li><strong>No warranty.</strong> The Apps are provided "as is", without warranty of any kind, to the fullest extent the law allows.</li>
                <li><strong>Limitation of liability.</strong> To the extent the law allows, we are not liable for indirect or consequential damages, and our total liability is limited as set out in the Standard EULA.</li>
                <li><strong>Export and law.</strong> You must follow export laws that apply to the Apps. The Standard EULA is governed by the laws of the State of California, unless the law of your country requires otherwise.</li>
            </ul>
        </>
    },
    {
        id: 'pro',
        title: 'CaptiVision Pro subscription',
        pricing: true,
        body: <>
            <p>
                CaptiVision Client is free. CaptiVision Host is free for seven days from the day you first open it
                on a device. After that, starting a session requires a CaptiVision Pro subscription. A session
                already running when the free week ends is not cut off.
            </p>
            <div className="cv-rowswrap">
                <table className="cv-rows">
                    <thead>
                        <tr><th> Plan </th><th> Price and terms </th></tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th> CaptiVision Pro, monthly </th>
                            <td> {monthly} per month, starting with a one-week free trial for eligible new subscribers. </td>
                        </tr>
                        <tr>
                            <th> CaptiVision Pro, yearly </th>
                            <td> {yearly} per year. No free trial. </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <ul>
                <li>Payment is charged to your Apple Account when you confirm the purchase, or at the end of the free trial if you started one.</li>
                <li>Your subscription renews automatically at the same price and length unless you turn off auto-renew at least 24 hours before the end of the current period. Your account is charged for renewal within 24 hours before the current period ends.</li>
                <li>You can manage or cancel your subscription in the Settings app: tap your name, then Subscriptions. On a Mac, open the App Store, click your name, then Account Settings. Cancelling stops the next renewal; you keep Pro until the end of the period you paid for.</li>
                <li>If you buy a subscription during a free trial, any unused part of the trial ends.</li>
                <li>Refunds are handled by Apple under its own policies. To request one, visit <a href={APP.reportAProblemUrl} rel="noopener noreferrer">reportaproblem.apple.com</a>.</li>
                <li>The prices above are the US prices, in US dollars. Prices vary by country or region: the App Store charges in your local currency and shows the exact price before you buy. We will tell you in advance if the price of your subscription changes.</li>
            </ul>
        </>
    },
    {
        id: 'use',
        title: 'Using CaptiVision responsibly',
        body: <>
            <p> CaptiVision films and listens to people. When you run a session, you agree to: </p>
            <ul>
                <li>Tell the people in the room that they are being filmed and captioned, and when a session is being recorded.</li>
                <li>Get any consent required by law, or by the policies of your school, employer or venue, especially where children are present.</li>
                <li>Only use phones whose owners have agreed to lend them. The owner of a phone can mute it or leave a session at any time.</li>
                <li>Not use CaptiVision to film or record anyone secretly, or for any unlawful purpose.</li>
            </ul>
        </>
    },
    {
        id: 'accuracy',
        title: 'An aid, not a guarantee',
        body: <p>
            Captions and speaker highlighting are produced automatically and can be wrong: words may be misheard,
            and a caption may be credited to the wrong person. CaptiVision is designed to help Deaf and hard of
            hearing people follow a conversation. It is not a replacement for a qualified sign language interpreter
            or professional captioner where one is needed or legally required, such as in medical, legal or
            emergency situations. Do not rely on CaptiVision as the only record of anything important without
            checking it.
        </p>
    },
    {
        id: 'third',
        title: 'Third-party components',
        body: <>
            <p> The Apps include or can download the following, each under its own license: </p>
            <ul>
                <li><strong>Whisper</strong> speech recognition models by OpenAI (MIT License), run on the iPad or Mac using whisper.cpp (MIT License), downloaded from Hugging Face if you choose them.</li>
                <li><strong>Light-ASD</strong> active speaker detection model by Liao and colleagues (MIT License).</li>
                <li>Apple's built-in speech recognition, provided by iPadOS and macOS under Apple's terms.</li>
            </ul>
        </>
    },
    {
        id: 'changes',
        title: 'Changes to these terms',
        body: <p>
            We may update these terms from time to time. We will post the new version here with a new effective
            date. If you keep using the Apps after a change, you accept the updated terms.
        </p>
    },
    {
        id: 'contact',
        title: 'Contact',
        body: <p>
            Questions, support or complaints: <strong>{LEGAL.companyLegalName}</strong>,
            {' '}<a href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a>.
        </p>
    }
].filter(section => SHOW_PRICING || !section.pricing)

const page = {
    title: 'Terms of Use',
    eyebrow: 'Legal',
    lede: SHOW_PRICING
        ? 'The license agreement for CaptiVision Client and CaptiVision Host, and the terms for CaptiVision Pro.'
        : 'The license agreement for CaptiVision Client and CaptiVision Host.',
    meta: `Effective ${formatDate('en', LEGAL.effectiveDate)}`,
    body: <CVSections sections={SECTIONS} />
}

export default page;
